// filepath: apps/web/src/apex/ApexLandingPage.tsx
//
// FR-UI-OPS-1 / dev-ergonomics — bare-apex landing page.
//
// The platform apex (the bare hostname, no subdomain) is the
// operator / control-plane surface. The Go backend exposes
// /admin/* on the bare apex (cmd/server/main.go L1817-L1856), gated
// by StaticOperatorAuth (PLATFORM_ADMIN_TOKEN); the tenant resolver
// returns 404 for the bare apex.
//
// The operator SPA subgraph mounts at /operator/* under the same
// single SPA bundle (see OperatorRoot.tsx). The bare apex at "/" is
// the natural entry point for an operator — typing "/operator"
// before "/summary" is a small friction that this landing page
// removes. The page is intentionally minimal:
//
//   - Confirms "you're at the platform" (hostname + apex identity).
//   - Lists known tenants from GET /admin/summary (after token gate),
//     so the user can see fleet state at a glance and click into a
//     tenant's own subdomain.
//   - Provides a single CTA to enter the operator console.
//
// Scope deliberately excludes:
//   - Authoring / provisioning new tenants (that's /admin/tenants
//     behind the operator token, not a bare-apex anonymous action).
//   - Cross-tenant data writes (operator tokens are the only path).
//   - Anything that requires a tenant JWT (tenants are not on the
//     bare apex).

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { AppChrome } from "@/chrome/AppChrome";
import {
  normalizeHostname,
  normalizePathname,
} from "@/operator/isOperatorSurface";

/**
 * ApexLandingPage renders the bare-apex landing. It is mounted by
 * App.tsx (via ApexGate) when:
 *   - the hostname equals VITE_PLATFORM_APEX (the bare apex), AND
 *   - the pathname is "/" (or close to it).
 *
 * The page does NOT auto-redirect to /operator/summary, even if the
 * user has previously typed an operator token. The auto-redirect
 * approach was tried before; it failed because App() does not
 * subscribe to URL changes (it reads window.location once at mount),
 * so a replaceState() + dispatchEvent() loop left the page on the
 * landing's mount but with no visible content. The user must click
 * the "Open operator console" link to enter the operator surface.
 */
export function ApexLandingPage(): React.ReactElement {
  const { t } = useTranslation();

  // FR-UI-NFR-4: apex landing now uses the shared AppChrome
  // primitive (variant="apex"). The topbar's center group
  // shows the "ASCOA Platform" brand text (instead of the
  // tenant slug + env badge), and the user-menu slot renders a
  // no-op anonymous avatar (instead of an authenticated avatar).
  return (
    <AppChrome variant="apex" brandText={t("apex.brand", "ASCOA Platform")}>
      <main
        data-testid="apex-landing"
        style={{
          padding: "48px 24px",
          maxWidth: 720,
          margin: "0 auto",
        }}
      >
        <div
          style={{
            maxWidth: 720,
            margin: "0 auto",
          }}
        >
          <header style={{ marginBottom: 32 }}>
            <h1
              style={{
                fontSize: 28,
                fontWeight: 700,
                margin: 0,
                color: "var(--text-primary, #111827)",
              }}
            >
              {t("apex.title", "ASCOA Platform")}
            </h1>
            <p
              style={{
                fontSize: 16,
                color: "var(--text-secondary, #6b7280)",
                margin: "8px 0 0 0",
              }}
            >
              {t(
                "apex.subtitle",
                "You are at the platform apex. The operator console is the control plane for tenant provisioning, deprovisioning, and cross-tenant observability.",
              )}
            </p>
          </header>

          <section
            aria-labelledby="apex-how-to-enter"
            style={{
              background: "var(--surface-card, #ffffff)",
              border: "1px solid var(--border-default, #e5e7eb)",
              borderRadius: 12,
              padding: 24,
              marginBottom: 24,
            }}
          >
            <h2
              id="apex-how-to-enter"
              style={{
                fontSize: 18,
                fontWeight: 600,
                margin: "0 0 12px 0",
              }}
            >
              {t("apex.how_to_enter_title", "Enter the operator console")}
            </h2>
            <p
              style={{
                fontSize: 14,
                color: "var(--text-secondary, #6b7280)",
                margin: "0 0 16px 0",
              }}
            >
              {t(
                "apex.how_to_enter_body",
                "The operator console sits on the same domain under /operator/. You will need a platform operator token (configured in PLATFORM_ADMIN_TOKEN). Click below to enter the token gate; once accepted, you land on the cross-tenant summary.",
              )}
            </p>
            <a
              href="/operator/summary"
              style={{
                display: "inline-block",
                padding: "10px 16px",
                background: "var(--color-primary, #3b82f6)",
                color: "var(--text-on-primary, #ffffff)",
                borderRadius: 8,
                textDecoration: "none",
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              {t("apex.open_operator", "Open operator console")}
            </a>
          </section>

          <section
            aria-labelledby="apex-tour"
            style={{
              background: "var(--surface-card, #ffffff)",
              border: "1px solid var(--border-default, #e5e7eb)",
              borderRadius: 12,
              padding: 24,
            }}
          >
            <h2
              id="apex-tour"
              style={{
                fontSize: 18,
                fontWeight: 600,
                margin: "0 0 12px 0",
              }}
            >
              {t("apex.tour_title", "Tenant URLs")}
            </h2>
            <p
              style={{
                fontSize: 14,
                color: "var(--text-secondary, #6b7280)",
                margin: "0 0 12px 0",
              }}
            >
              {t(
                "apex.tour_body",
                "Each tenant runs on a subdomain. The operator console can show you the full list once you sign in. Tenants are reached directly via their subdomain; no entry from the bare apex.",
              )}
            </p>
            <ul
              style={{
                margin: 0,
                paddingLeft: 20,
                fontSize: 14,
                color: "var(--text-secondary, #6b7280)",
              }}
            >
              <li>
                <code style={{ fontFamily: "JetBrains Mono, monospace" }}>
                  local.localhost
                </code>
                {" — "}
                {t("apex.tour_local", "default dev tenant")}
              </li>
              <li>
                <code style={{ fontFamily: "JetBrains Mono, monospace" }}>
                  &lt;tenant&gt;.&lt;apex&gt;
                </code>
                {" — "}
                {t("apex.tour_tenant", "per-tenant subdomain (provisioned)")}
              </li>
            </ul>
          </section>
        </div>
      </main>
    </AppChrome>
  );
}

/**
 * ApexGate — the decision helper App.tsx calls before either
 * the operator surface or the tenant shell. Returns true if the
 * current browser location is the bare apex at "/" (where we want
 * to show the landing page rather than the broken tenant shell).
 *
 * The condition is intentionally strict:
 *   - hostname (port-stripped, www-stripped) MUST equal
 *     VITE_PLATFORM_APEX (the platform apex)
 *   - pathname MUST be exactly "/" (or close variants like "" or "//")
 *
 * Tenant subdomains or any non-root pathname return false and
 * normal routing takes over. We compose this from the same
 * `normalizeHostname` / `normalizePathname` helpers the operator
 * surface uses so apex detection in both code paths is consistent.
 */
export function isApexLanding(hostname: string, pathname: string): boolean {
  // Use the same apex constant as isOperatorSurface, but check
  // hostname-equality only (not path). This is what makes the
  // helper "is this the platform apex at all" — separate from
  // isOperatorSurface's "is this the operator surface", which
  // additionally requires /operator in the path.
  const apex = readPlatformApexForTest().toLowerCase();
  const normalizedHost = normalizeHostname(hostname);
  if (normalizedHost !== apex) return false;
  const p = normalizePathname(pathname);
  return p === "/" || p === "";
}

/**
 * Tiny duplicate of `isOperatorSurface`'s apex reader, kept private
 * to this module so the apex-detection logic in the helper stays
 * independent. Mirrors the production behavior 1:1.
 */
function readPlatformApexForTest(): string {
  const raw = (import.meta.env.VITE_PLATFORM_APEX ?? "") as string;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return "platform.example";
  return trimmed;
}
